import { z } from 'zod';

/**
 * FRS §8.6.6 — every collection endpoint paginates. There is no unbounded list.
 *
 * `page` is 1-based, `page_size` defaults to 25 and is capped at 100. Sorting
 * uses `?sort=field` with a `-` prefix for descending, and only allow-listed
 * fields are sortable — an arbitrary column name from a client is both an
 * information leak and a trivial way to force a sequential scan.
 */

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  page_size: z.coerce.number().int().positive().max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export interface SortSpec<TField extends string> {
  field: TField;
  direction: 'asc' | 'desc';
}

/**
 * Builds a `?sort=` parser bound to an explicit allow-list.
 *
 * Passing the allow-list is mandatory — there is no "sort by anything" variant,
 * because that is how an unsortable column becomes sortable by accident.
 */
export function sortQuerySchema<const TFields extends readonly [string, ...string[]]>(
  allowed: TFields,
  fallback: SortSpec<TFields[number]>,
) {
  return z
    .string()
    .optional()
    .transform((raw, ctx): SortSpec<TFields[number]> => {
      if (!raw) return fallback;

      const direction = raw.startsWith('-') ? 'desc' : 'asc';
      const field = raw.startsWith('-') ? raw.slice(1) : raw;

      if (!allowed.includes(field)) {
        ctx.addIssue({
          code: 'custom',
          message: `Cannot sort by "${field}". Sortable fields: ${allowed.join(', ')}.`,
        });
        return fallback;
      }

      return { field: field as TFields[number], direction };
    });
}

export function paginationMeta(page: number, pageSize: number, totalItems: number) {
  return {
    page,
    page_size: pageSize,
    total_items: totalItems,
    total_pages: pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0,
  };
}
